type IntegrationLike = {
    provider?: string;
    external_account_name?: string | null;
    externalAccountName?: string | null;
    mappings?: Array<Record<string, any>>;
    [key: string]: any;
};

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const normalizeProviderKey = (provider?: string) => String(provider || '').trim().toLowerCase();

const getReadableValue = (value: unknown): string | null => {
    if (value == null) {
        return null;
    }

    const text = String(value).trim();
    if (!text || UUID_PATTERN.test(text)) {
        return null;
    }

    return text;
};

const getIdentityValues = (mapping?: Record<string, any>) => {
    if (!mapping) {
        return [];
    }

    return [
        mapping.id,
        mapping.slugId,
        mapping.slug_id,
        mapping.connection_id,
        mapping.account_id,
        mapping.external_account_id,
        mapping.externalAccountId,
    ].map(value => String(value || '').trim()).filter(Boolean);
};

const getGoogleAdsReadableAccountName = (
    integration: IntegrationLike,
    mapping?: Record<string, any>
) => [
    mapping?.external_account_name,
    mapping?.externalAccountName,
    integration.external_account_name,
    integration.externalAccountName,
    mapping?.authorized_account_email,
    mapping?.authorizedAccountEmail,
    mapping?.external_account_email,
    mapping?.externalAccountEmail,
    mapping?.authorized_account_name,
    mapping?.authorizedAccountName,
    mapping?.account_name,
].map(getReadableValue).find(Boolean) || null;

export const getGoogleOAuthAccountDisplayName = (
    integration: IntegrationLike,
    mapping?: Record<string, any>,
    profile?: Record<string, any>,
): string | null => {
    const connectionIdentityValues = new Set([
        ...getIdentityValues(mapping),
        integration.connection_id,
        integration.nango_connection_id,
    ].map(value => String(value || '').trim().toLowerCase()).filter(Boolean));

    return [
        mapping?.external_account_name,
        mapping?.externalAccountName,
        mapping?.authorized_account_name,
        mapping?.authorizedAccountName,
        mapping?.authorized_account_email,
        mapping?.authorizedAccountEmail,
        mapping?.external_account_email,
        mapping?.externalAccountEmail,
        integration.external_account_name,
        integration.externalAccountName,
        mapping?.account_name,
        profile?.accountName,
        profile?.username,
    ].map(getReadableValue).find(value => value && !connectionIdentityValues.has(value.toLowerCase())) || null;
};

export const preserveGoogleAdsExternalAccountNames = <T extends IntegrationLike>(
    nextIntegrations: T[],
    previousIntegrations: IntegrationLike[]
): T[] => {
    const previousNameByIdentity = new Map<string, string>();

    previousIntegrations
        .filter(integration => normalizeProviderKey(integration.provider) === 'google-ads')
        .forEach(integration => {
            (integration.mappings || []).forEach(mapping => {
                const readableName = getGoogleAdsReadableAccountName(integration, mapping);
                if (!readableName) {
                    return;
                }

                getIdentityValues(mapping).forEach(identity => {
                    previousNameByIdentity.set(identity, readableName);
                });
            });
        });

    if (previousNameByIdentity.size === 0) {
        return nextIntegrations;
    }

    return nextIntegrations.map(integration => {
        if (normalizeProviderKey(integration.provider) !== 'google-ads') {
            return integration;
        }

        let integrationFallbackName: string | null = null;
        const mappings = (integration.mappings || []).map(mapping => {
            if (getGoogleAdsReadableAccountName(integration, mapping)) {
                return mapping;
            }

            const preservedName = getIdentityValues(mapping)
                .map(identity => previousNameByIdentity.get(identity))
                .find(Boolean);

            if (!preservedName) {
                return mapping;
            }

            integrationFallbackName ||= preservedName;
            return {
                ...mapping,
                external_account_name: preservedName,
                externalAccountName: preservedName,
            };
        });

        if (!integrationFallbackName) {
            return integration;
        }

        return {
            ...integration,
            external_account_name: integration.external_account_name || integrationFallbackName,
            externalAccountName: integration.externalAccountName || integrationFallbackName,
            mappings,
        };
    });
};
